package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.capacity.CapacitySummary
import com.evoapps.evoshift.domain.capacity.monthRange
import com.evoapps.evoshift.domain.capacity.within
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.domain.usecase.GetAvailabilityRequestUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.capacity.GetRosterCapacityUseCase
import com.evoapps.evoshift.domain.usecase.capacity.RemindAvailabilityUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterUseCase
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.capacity_reminders_sent
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

data class RosterCapacityUiState(
    val isLoading: Boolean = true,
    val year: Int = 0,
    val month: Int = 0,
    /** The days counted; the request's period at first (the whole month without one). */
    val range: ScheduleRange? = null,
    val summary: CapacitySummary? = null,
    /** What the person may do at the workplace: capacity needs [Gate.CAPACITY], reminders [Gate.AVAILABILITY_CAMPAIGN]. */
    val capabilities: Set<Capability> = emptySet(),
    /** People reminded on this screen (shown at once; taken back if sending fails). */
    val reminded: Set<String> = emptySet(),
    val isReminding: Boolean = false,
    val error: UiText? = null,
    /** A one-off note for the snackbar. */
    val message: UiText? = null
) {
    val wholeMonth: ScheduleRange? get() = if (year > 0) monthRange(year, month) else null
    /** Reminders can be sent: a manager while the plan runs, someone left to remind. */
    val canRemind: Boolean get() = capabilities.shows(Gate.AVAILABILITY_CAMPAIGN) && remindable.isNotEmpty()
    /** Missing people a reminder reaches and who were not reminded here yet. */
    val remindable: List<String> get() = summary?.remindable?.map { it.userId }?.filter { it !in reminded }.orEmpty()
}

sealed interface RosterCapacityEvent {
    /** Count the days [start]–[end] of the month (cut to it). */
    data class SetRange(val start: LocalDate, val end: LocalDate) : RosterCapacityEvent
    data object WholeMonth : RosterCapacityEvent
    /**
     * Remind everyone who has not sent their availability (and has the app). [title] and [message]
     * are the push text in the sender's language (the app shows its own from the data).
     */
    data class RemindMissing(val title: String, val message: String) : RosterCapacityEvent
    data object MessageShown : RosterCapacityEvent
}

/**
 * Capacity before planning a month (SH-48): demand from the skeleton, the team's availability,
 * contract targets and the working-time norm, for the whole month or a range of its days, and
 * who has not answered (with a reminder). Managers see it; contracts and targets are read only
 * for those who may see pay terms (owner, financial managers).
 */
class RosterCapacityViewModel(
    private val rosterId: String,
    private val getRoster: GetRosterUseCase,
    private val getCapacity: GetRosterCapacityUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getAvailabilityRequest: GetAvailabilityRequestUseCase? = null,
    private val remindAvailability: RemindAvailabilityUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(RosterCapacityUiState())
    val uiState = _uiState.asStateFlow()

    private var loading: Job? = null
    private var viewed = false

    init {
        start()
    }

    private fun start() {
        viewModelScope.launch {
            val workplaceId = RosterIds.workplaceIdOf(rosterId)
            val user = getCurrentUser().first()
            val capabilities = user?.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId)).orEmpty()
            _uiState.update { it.copy(capabilities = capabilities) }
            if (!capabilities.shows(Gate.CAPACITY)) {
                _uiState.update { it.copy(isLoading = false) }
                return@launch
            }
            val roster = getRoster(rosterId).getOrElse { e ->
                _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                return@launch
            }
            val month = monthRange(roster.year, roster.month)
            // The request's period first: what the team was asked about.
            val period = roster.availabilityRequestId?.let { getAvailabilityRequest?.invoke(it)?.getOrNull() }?.period
            val asked = period?.let {
                runCatching { ScheduleRange(LocalDate.parse(it.startDate.take(10)), LocalDate.parse(it.endDate.take(10))) }.getOrNull()
            }?.within(month)
            _uiState.update { it.copy(year = roster.year, month = roster.month, range = asked ?: month) }
            load()
        }
    }

    private fun load() {
        val state = _uiState.value
        loading?.cancel()
        loading = viewModelScope.launch {
            // The figures stay on screen while another range loads.
            _uiState.update { it.copy(isLoading = it.summary == null, error = null) }
            getCapacity(rosterId, state.range, readPayTerms = state.capabilities.shows(Gate.PAY_AND_PRIVATE_DATA)).fold(
                onSuccess = { summary ->
                    _uiState.update { it.copy(isLoading = false, summary = summary, range = summary.range) }
                    if (!viewed) {
                        viewed = true
                        AppTelemetry.log(AnalyticsEvent.CapacityViewed)
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
            )
        }
    }

    fun onEvent(event: RosterCapacityEvent) {
        when (event) {
            is RosterCapacityEvent.SetRange -> setRange(event.start, event.end)
            RosterCapacityEvent.WholeMonth -> _uiState.value.wholeMonth?.let { setRange(it.start, it.endInclusive) }
            is RosterCapacityEvent.RemindMissing -> remindMissing(event.title, event.message)
            RosterCapacityEvent.MessageShown -> _uiState.update { it.copy(message = null) }
        }
    }

    private fun setRange(start: LocalDate, end: LocalDate) {
        val month = _uiState.value.wholeMonth ?: return
        if (end < start) return
        val range = ScheduleRange(start, end).within(month) ?: return
        if (range == _uiState.value.range && _uiState.value.summary != null) return
        _uiState.update { it.copy(range = range) }
        load()
    }

    /** Optimistic: the people show as reminded at once and go back to "missing" if sending fails. */
    private fun remindMissing(title: String, message: String) {
        val state = _uiState.value
        val remind = remindAvailability ?: return
        if (!state.canRemind || state.isReminding) return
        val ids = state.remindable
        _uiState.update { it.copy(reminded = it.reminded + ids, isReminding = true) }
        viewModelScope.launch {
            remind(rosterId, ids, title, message).fold(
                onSuccess = { count ->
                    AppTelemetry.log(AnalyticsEvent.AvailabilityReminderSent)
                    _uiState.update { it.copy(isReminding = false, message = UiText.Resource(Res.string.capacity_reminders_sent, listOf(count))) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isReminding = false, reminded = it.reminded - ids.toSet(), message = e.toUiText()) }
                }
            )
        }
    }
}
